<script setup lang="ts">
import { onMounted } from "vue"
import { Head, Link, useForm } from "@inertiajs/vue3"
import { notify } from "@kyvg/vue3-notification"
import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from "@/types/PageHeading"
import Button from "@/Components/Utils/Button.vue"
import InputNumber from "primevue/inputnumber"

const props = defineProps<{
    title?: string
    pageHead: PageHeadingTS
    setting: {
        plate_price: number
        print_price: number
        pound_price: number
        knife_price: number
        target_profit: number
        safe_margin: number
        material_rounding_multiple: number
        offer_note: string | null
    }
    back_url: string | null
}>()

const form = useForm({
    plate_price: props.setting.plate_price,
    print_price: props.setting.print_price,
    pound_price: props.setting.pound_price,
    knife_price: props.setting.knife_price,
    target_profit: props.setting.target_profit,
    safe_margin: props.setting.safe_margin,
    material_rounding_multiple: props.setting.material_rounding_multiple,
    offer_note: props.setting.offer_note ?? "",
})

type CostField = "plate_price" | "print_price" | "pound_price" | "knife_price" | "target_profit"

const costFields: { key: CostField, label: string, hint: string }[] = [
    { key: "plate_price", label: "Harga plat", hint: "Biaya plat per kerangka" },
    { key: "print_price", label: "Harga cetak", hint: "Biaya cetak per kerangka" },
    { key: "pound_price", label: "Harga pound", hint: "Biaya pound per kerangka" },
    { key: "knife_price", label: "Harga pisau", hint: "Biaya pisau per kerangka" },
    { key: "target_profit", label: "Target profit", hint: "Target profit per kerangka" },
]

const submit = () => {
    form.patch(route("custom-size.settings.update", props.back_url ? { back: props.back_url } : {}), {
        preserveScroll: true,
        onSuccess: () =>
            notify({
                title: "Tersimpan",
                text: "Pengaturan custom size berhasil diperbarui.",
                type: "success",
            }),
    })
}

const isNoteFocused = window.location.hash === "#catatan"

onMounted(() => {
    if (isNoteFocused) {
        document.getElementById("catatan")?.scrollIntoView({ behavior: "smooth", block: "center" })
    }
})
</script>

<template>

    <Head :title="title" />
    <PageHeading :data="pageHead">
        <template #otherBefore>
            <Link v-if="back_url" :href="back_url">
                <Button type="tertiary" icon="far fa-arrow-left" label="Kembali ke penawaran" />
            </Link>
        </template>
    </PageHeading>

    <div class="mx-auto max-w-4xl px-4 py-6">
        <section class="rounded-xl border border-gray-200 bg-white p-6">
            <div class="mb-4">
                <h3 class="font-semibold text-gray-700">Biaya default</h3>
                <p class="text-xs text-gray-400">
                    Nilai awal untuk perhitungan baru di Index Custom Size. Perubahan di halaman perhitungan tidak mengubah pengaturan ini.
                </p>
            </div>

            <div class="grid gap-4 md:grid-cols-3">
                <div v-for="field in costFields" :key="field.key">
                    <label class="mb-1 block text-sm text-gray-600">{{ field.label }} <span class="text-red-500">*</span></label>
                    <InputNumber showButtons :step="100" v-model="form[field.key]" mode="currency" currency="IDR" locale="id-ID"
                        :maxFractionDigits="0" :min="0" fluid :class="form.errors[field.key] ? 'errorShake' : ''"
                        @input="form.clearErrors(field.key)" />
                    <div class="mt-0.5 text-xs text-gray-400">{{ field.hint }}</div>
                    <div v-if="form.errors[field.key]" class="mt-1 text-xs text-red-500">{{ form.errors[field.key] }}</div>
                </div>
            </div>
        </section>

        <section class="mt-4 rounded-xl border border-gray-200 bg-white p-6">
            <div class="mb-4">
                <h3 class="font-semibold text-gray-700">Layout & pembulatan</h3>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
                <div>
                    <label class="mb-1 flex items-center gap-x-1.5 text-sm text-gray-600">
                        <span class="inline-block size-2.5 rounded-sm border border-yellow-700 bg-[#fff200]" />
                        Jarak aman <span class="text-red-500">*</span>
                    </label>
                    <InputNumber showButtons :step="0.1" v-model="form.safe_margin" :min="0" :maxFractionDigits="2" locale="id-ID" suffix=" cm"
                        fluid :class="form.errors.safe_margin ? 'errorShake' : ''"
                        inputClass="!border-yellow-500 !bg-[#fff200]/30" @input="form.clearErrors('safe_margin')" />
                    <div class="mt-0.5 text-xs text-gray-400">Jarak aman pada setiap sisi kerangka</div>
                    <div v-if="form.errors.safe_margin" class="mt-1 text-xs text-red-500">{{ form.errors.safe_margin }}</div>
                </div>

                <div>
                    <label class="mb-1 block text-sm text-gray-600">Pembulatan biaya bahan <span class="text-red-500">*</span></label>
                    <InputNumber showButtons :step="100" v-model="form.material_rounding_multiple" mode="currency" currency="IDR" locale="id-ID"
                        :maxFractionDigits="0" :min="0" fluid
                        :class="form.errors.material_rounding_multiple ? 'errorShake' : ''"
                        @input="form.clearErrors('material_rounding_multiple')" />
                    <div class="mt-0.5 text-xs text-gray-400">
                        Dibulatkan ke atas ke kelipatan ini, mis. 100: Rp683,33 menjadi Rp700. Isi 0 tanpa pembulatan.
                    </div>
                    <div v-if="form.errors.material_rounding_multiple" class="mt-1 text-xs text-red-500">
                        {{ form.errors.material_rounding_multiple }}
                    </div>
                </div>
            </div>
        </section>

        <section id="catatan" class="mt-4 rounded-xl border bg-white p-6"
            :class="isNoteFocused ? 'border-indigo-300 ring-2 ring-indigo-200' : 'border-gray-200'">
            <div class="mb-4">
                <h3 class="font-semibold text-gray-700">Catatan penawaran</h3>
                <p class="text-xs text-gray-400">Ditambahkan otomatis di bagian "Catatan" pada teks penawaran.</p>
            </div>
            <textarea v-model="form.offer_note" rows="6" class="w-full rounded-md border-gray-300 text-sm"
                placeholder="Harga belum termasuk ongkos kirim" />
        </section>

        <div class="mt-4 flex justify-end">
            <Button type="save" :loading="form.processing" label="Simpan" @click="submit" />
        </div>
    </div>
</template>
